<!--
  PackEditPage · 课程包编辑页（路由 /packs/:id/edit）
  功能：
    · 编辑课程包名称/描述（保存到后端）
    · 列出该课程包下所有课程（含 upload_step 进度状态）
    · 上传新视频（MD5 去重 + 步骤持久化 + 实时进度）
    · 点击课程进入播放器 /lessons/:id/play
    · 删除课程（同步清理磁盘文件）
  上传步骤持久化：
    0=待处理 / 1=抽音频 / 2=ASR处理 / 3=格式化 / 4=完成
    每步都写 DB，刷新页面/重启服务后状态不丢；同 MD5 重传会续跑或复用
-->
<template>
  <div class="pack-edit-page">
    <!-- 顶部：返回 + 编辑课程包信息 -->
    <div class="page-head">
      <button @click="$router.push('/packs')">← 返回课程包列表</button>
      <h2 v-if="!editing">📁 {{ pack.name || '加载中…' }}</h2>
      <input v-else v-model.trim="editName" placeholder="课程包名称" maxlength="60" style="flex:0 1 320px" />
      <button v-if="!editing" @click="startEdit">✏️ 编辑</button>
      <template v-else>
        <button @click="saveEdit" class="primary">保存</button>
        <button @click="editing = false">取消</button>
      </template>
    </div>

    <div v-if="pack.desc || editing" class="pack-desc-row">
      <template v-if="!editing">
        <span v-if="pack.desc" class="mute">{{ pack.desc }}</span>
      </template>
      <input v-else v-model.trim="editDesc" placeholder="课程包描述（可选）" maxlength="120" style="flex:1" />
    </div>

    <!-- 上传区 -->
    <section class="upload-section">
      <div class="section-head">
        <b>📤 上传新视频到本课程包</b>
        <button class="primary" @click="fileInput?.click()">选择视频文件</button>
        <input type="file" accept="video/*" ref="fileInput" style="display:none" @change="onFilePicked" />
      </div>
      <div class="hint">
        同一文件（MD5 相同）会自动去重：已完成 → 直接复用；中断 → 自动续跑，不会从头来。
      </div>
      <UploadStatusBar
        :show="upShow"
        :status-text="upText"
        :percent="upPercent"
        :error-mode="upError"
      />
    </section>

    <!-- 课程列表 -->
    <section class="lessons-section">
      <div class="section-head">
        <b>🎬 本课程包的课程（{{ lessons.length }} 节）</b>
        <div class="section-actions">
          <button @click="onImportClick" :disabled="importing" class="import-btn" title="从另一台电脑导出的 zip 包导入课程">
            {{ importing ? (importPercent > 0 ? `⏳ ${importPercent}%` : '⏳ 导入中…') : '📥 导入课程' }}
          </button>
          <input ref="importFileInput" type="file" accept=".zip"
                 style="display:none" @change="onImportFile" />
          <button @click="loadLessons" :disabled="lessonsLoading">↻ 刷新</button>
        </div>
      </div>

      <!-- 导入进度条 -->
      <div v-show="importing" class="io-progress-box">
        <div class="io-progress-row">
          <span class="io-chip" :class="{error: importError, done: importPhase === 'done'}">
            {{ importText || '导入中…' }}
          </span>
        </div>
        <div class="io-progress">
          <span
            :style="{
              width: importPercent + '%',
              background: importError
                ? 'linear-gradient(90deg,#f87171,#b45)'
                : importPhase === 'done'
                  ? 'linear-gradient(90deg,#4ade80,#16a34a)'
                  : '',
            }"
          ></span>
        </div>
      </div>

      <div v-if="lessonsLoading" class="empty">加载中…</div>
      <div v-else-if="!lessons.length" class="empty">
        还没有课程，上传第一个视频开始学习吧 🚀
      </div>
      <table v-else class="lessons-table">
        <thead>
          <tr>
            <th>#</th>
            <th>课程标题</th>
            <th>句子数</th>
            <th>时长</th>
            <th>处理状态</th>
            <th>MD5</th>
            <th>创建时间</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(l, i) in lessons" :key="l.id">
            <td>{{ i + 1 }}</td>
            <td class="title-cell" :title="l.title">{{ l.title || '未命名' }}</td>
            <td>{{ l.sentence_count || 0 }}</td>
            <td>{{ fmtTime(l.duration_sec || 0) }}</td>
            <td>
              <span v-if="(l.upload_step || 0) >= 4" class="learn-chip" :class="learnClass(l)">
                {{ learnText(l) }}
              </span>
              <span v-else class="step-chip" :class="stepClass(l.upload_step)">
                {{ stepText(l.upload_step) }}
              </span>
            </td>
            <td class="md5-cell" :title="l.file_md5">{{ l.file_md5 ? l.file_md5.slice(0, 8) : '—' }}</td>
            <td class="date-cell">{{ fmtDate(l.created_at) }}</td>
            <td class="op-cell">
              <div class="op-stack">
                <div class="op-btns">
                  <button
                    :disabled="(l.upload_step || 0) < 4"
                    @click="onPlay(l)"
                    :title="(l.upload_step || 0) < 4 ? '处理未完成' : '播放'"
                  >▶ 播放</button>
                  <button
                    v-if="(l.upload_step || 0) >= 4"
                    @click="onExport(l)"
                    :disabled="!!exportingId[l.id]"
                    class="export-btn"
                    title="导出为 zip（含视频+字幕，可拷到另一台电脑导入播放）"
                  >{{ exportingId[l.id]
                      ? (exportText[l.id] || (exportPercent[l.id] > 0
                          ? `⏳ 导出中 ${exportPercent[l.id]}%`
                          : '⏳ 打包中…'))
                      : '📤 导出' }}</button>
                  <button
                    v-if="(l.upload_step || 0) < 4"
                    @click="onRetry(l)"
                    :disabled="!!retryingId[l.id]"
                    class="warn-btn"
                    :title="(l.upload_step || 0) === 0 ? '从头开始处理' : '继续处理（中断续跑）'"
                  >{{ retryingId[l.id] ? '⏳ 续跑中…' : '↻ 续跑' }}</button>
                  <button @click="onDelete(l)" class="danger-btn" title="删除课程">🗑</button>
                </div>
                <div v-if="exportingId[l.id]" class="export-progress">
                  <div class="mini-progress">
                    <span
                      :style="{
                        width: (exportPercent[l.id] || 0) + '%',
                        background: exportPhase[l.id] === 'done'
                          ? 'linear-gradient(90deg,#4ade80,#16a34a)'
                          : '',
                      }"
                    ></span>
                  </div>
                  <span class="export-pct">{{ Math.round(exportPercent[l.id] || 0) }}%</span>
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </section>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import UploadStatusBar from '@/components/UploadStatusBar.vue'
import { api, toast, fmtTime } from '@/composables/usePlayerState'

const props = defineProps({ id: { type: String, default: '' } })
const route = useRoute()
const router = useRouter()

const packId = props.id || route.params.id

const pack = ref({})
const lessons = ref([])
const lessonsLoading = ref(false)
const retryingId = ref({})
const exportingId = ref({})
const exportPercent = ref({})
const exportPhase = ref({})   // 'packing' | 'download' | 'done'
const exportText = ref({})    // 每行独立状态文案
const importing = ref(false)
const importPercent = ref(0)
const importPhase = ref('')   // 'upload' | 'process' | 'done' | ''
const importText = ref('')
const importError = ref(false)
const importFileInput = ref(null)

// 编辑
const editing = ref(false)
const editName = ref('')
const editDesc = ref('')

// 上传
const fileInput = ref(null)
const upShow = ref(false)
const upText = ref('待上传…')
const upPercent = ref(0)
const upError = ref(false)

// ============== 数据加载 ==============
async function loadPack() {
  try {
    pack.value = await api('/api/course-packs/' + packId)
  } catch (e) {
    toast('加载课程包失败：' + e.message)
  }
}

async function loadLessons() {
  lessonsLoading.value = true
  try {
    const data = await api('/api/course-packs/' + packId + '/lessons')
    lessons.value = data.items || []
  } catch (e) {
    toast('加载课程列表失败：' + e.message)
  } finally {
    lessonsLoading.value = false
  }
}

// ============== 编辑课程包 ==============
function startEdit() {
  editName.value = pack.value.name || ''
  editDesc.value = pack.value.desc || ''
  editing.value = true
}
async function saveEdit() {
  if (!editName.value) { toast('名称不能为空'); return }
  try {
    const fd = new FormData()
    fd.append('name', editName.value)
    fd.append('desc', editDesc.value)
    await api('/api/course-packs/' + packId, { method: 'PUT', body: fd })
    pack.value.name = editName.value
    pack.value.desc = editDesc.value
    editing.value = false
    toast('已保存')
  } catch (e) {
    toast('保存失败：' + e.message)
  }
}

// ============== 上传 + 轮询 ==============
function onFilePicked(e) {
  const f = e.target.files?.[0]
  e.target.value = ''
  if (f) startUpload(f)
}

async function startUpload(file) {
  upShow.value = true; upPercent.value = 0
  upText.value = '上传中…'; upError.value = false
  try {
    const fd = new FormData()
    fd.append('video', file)
    fd.append('pack_id', packId)
    upPercent.value = 15
    const r = await api('/api/transcribe', { method: 'POST', body: fd })
    upPercent.value = 30
    upText.value = r.dedup
      ? `⚡ ${r.message}（MD5 命中）· ${r.jobId.slice(0, 8)}…`
      : `任务已提交 (${r.jobId.slice(0, 8)}…) · 抽音频中…`

    // 即便 dedup，也要轮询直到 done（dedup+已完成的情况后端会立即返回 done）
    const jobId = r.jobId
    const maxPoll = 60 * 60 * 4
    let i = 0
    while (i++ < maxPoll) {
      await new Promise(r => setTimeout(r, 1200))
      const job = await api('/api/jobs/' + jobId)
      const p = Math.max(30, Math.min(95, 30 + (job.progress || 0) * 0.65))
      upPercent.value = p
      upText.value = `${job.status} · ${job.progress || 0}%${job.note ? '（' + job.note + '）' : ''}`
      if (job.status === 'done') {
        upPercent.value = 100
        upText.value = '✅ 完成！'
        toast('🎉 字幕生成完成')
        await loadLessons()
        setTimeout(() => { upShow.value = false }, 2500)
        return
      }
      if (job.status === 'error') throw new Error(job.error || '任务失败')
    }
  } catch (err) {
    upError.value = true
    upText.value = '❌ ' + err.message
    setTimeout(() => { upShow.value = false; upError.value = false }, 5000)
    toast('上传/转写失败：' + err.message, 3500)
  }
}

// ============== 课程操作 ==============
function onPlay(l) {
  router.push('/lessons/' + l.id + '/play')
}
async function onRetry(l) {
  try {
    retryingId.value = { ...retryingId.value, [l.id]: true }
    const res = await api('/api/lessons/' + l.id + '/retry', { method: 'POST' })
    toast(res.message || '已触发续跑，几秒后进度会开始更新')
    // 立刻刷新一次列表 + 触发 3s 轮询
    await loadLessons()
    // 触发 UploadStatusBar 轮询 jobs 进度（用户从顶部也能看到进度条）
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('sunny:poll-job', { detail: { jobId: l.id } }))
    }
  } catch (e) {
    toast('续跑失败：' + e.message)
  } finally {
    const next = { ...retryingId.value }
    delete next[l.id]
    retryingId.value = next
  }
}
async function onDelete(l) {
  if (!confirm(`确认删除课程「${l.title}」？\n视频/音频/字幕文件都会一起删掉，不可恢复。`)) return
  try {
    await api('/api/lessons/' + l.id, { method: 'DELETE' })
    toast('已删除')
    await loadLessons()
  } catch (e) {
    toast('删除失败：' + e.message)
  }
}

// ============== 导出 / 导入（跨设备同步） ==============
/** 从 Content-Disposition 解析文件名（支持 RFC 5987 filename*=UTF-8''… 中文）*/
function parseFilename(cd) {
  if (!cd) return null
  // 优先 filename*=UTF-8''...
  const mStar = cd.match(/filename\*=UTF-8''([^;]+)/i)
  if (mStar) {
    try { return decodeURIComponent(mStar[1].trim().replace(/^"|"$/g, '')) } catch {}
  }
  // 兜底 filename="..."
  const m = cd.match(/filename="([^"]+)"/)
  return m?.[1] ?? null
}

async function onExport(l) {
  const lessonId = l.id
  exportingId.value = { ...exportingId.value, [lessonId]: true }
  exportPercent.value = { ...exportPercent.value, [lessonId]: 0 }
  exportPhase.value = { ...exportPhase.value, [lessonId]: 'packing' }
  exportText.value = { ...exportText.value, [lessonId]: '📦 打包中（准备视频+字幕…）' }

  let packingTimer = null
  const clearPacking = () => {
    if (packingTimer) { clearInterval(packingTimer); packingTimer = null }
  }
  // 打包阶段：心跳式慢推到 ~45%，让用户能看到"在动"而不是卡死
  packingTimer = setInterval(() => {
    const cur = exportPercent.value[lessonId] || 0
    if (cur < 45) {
      const step = Math.max(1, Math.floor((45 - cur) / 20))
      exportPercent.value = { ...exportPercent.value, [lessonId]: Math.min(45, cur + step) }
      exportText.value = { ...exportText.value, [lessonId]: '📦 打包中（准备视频+字幕…）' }
    }
  }, 1500)

  try {
    const ok = await new Promise((resolve) => {
      const xhr = new XMLHttpRequest()
      xhr.open('GET', `/api/lessons/${lessonId}/export`, true)
      xhr.responseType = 'blob'
      xhr.timeout = 30 * 60 * 1000  // 最长 30 分钟

      let downloadStarted = false
      xhr.onprogress = (ev) => {
        // 第一个 progress 事件 = 打包完成，开始下载了
        if (!downloadStarted) {
          downloadStarted = true
          clearPacking()
          exportPhase.value = { ...exportPhase.value, [lessonId]: 'download' }
        }
        if (ev.lengthComputable) {
          // 总进度 = 打包占 45% + 下载占 55%
          const downloadPct = ev.loaded / ev.total
          const total = Math.round(45 + downloadPct * 55)
          exportPercent.value = { ...exportPercent.value, [lessonId]: total }
          const mb = (ev.loaded / 1024 / 1024).toFixed(1)
          exportText.value = { ...exportText.value, [lessonId]: `⬇️ 下载中 ${mb}MB` }
        } else {
          // 没有 Content-Length：慢推到 95% 等完成
          const cur = exportPercent.value[lessonId] || 45
          if (cur < 95) {
            exportPercent.value = { ...exportPercent.value, [lessonId]: Math.min(95, cur + 1) }
          }
          exportText.value = { ...exportText.value, [lessonId]: '⬇️ 下载中…' }
        }
      }
      xhr.onload = () => {
        clearPacking()
        if (xhr.status >= 200 && xhr.status < 300) {
          const blob = xhr.response
          const declaredSize = Number(xhr.getResponseHeader('Content-Length') || xhr.getResponseHeader('X-Zip-FileSize') || 0)
          // ✅ 0 字节防御：如果响应是 0 字节或远小于声明的大小，判定为失败
          if (!blob || blob.size === 0) {
            alert(`导出失败：下载的文件是 0 字节（后端打包异常或连接被中断）。请刷新页面后重试，如仍失败请联系管理员查看后端日志。`)
            resolve(false); return
          }
          if (declaredSize > 0 && blob.size < declaredSize * 0.8) {
            if (!confirm(`警告：下载的文件大小（${(blob.size/1024/1024).toFixed(1)}MB）远小于预期（${(declaredSize/1024/1024).toFixed(1)}MB），可能不完整。是否仍然保存？`)) {
              resolve(false); return
            }
          }
          const cd = xhr.getResponseHeader('Content-Disposition') || ''
          const filename = parseFilename(cd) || `${l.title || lessonId}.zip`
          const a = document.createElement('a')
          a.href = URL.createObjectURL(blob)
          a.download = filename
          document.body.appendChild(a)
          a.click()
          document.body.removeChild(a)
          setTimeout(() => URL.revokeObjectURL(a.href), 5000)
          exportPhase.value = { ...exportPhase.value, [lessonId]: 'done' }
          exportPercent.value = { ...exportPercent.value, [lessonId]: 100 }
          const mb = (blob.size / 1024 / 1024).toFixed(1)
          exportText.value = { ...exportText.value, [lessonId]: `✅ 已下载 ${mb}MB` }
          resolve(true)
        } else {
          const reader = new FileReader()
          reader.onload = () => {
            const msg = reader.result || `HTTP ${xhr.status}`
            console.error('[export fail]', msg)
            alert(`导出失败：${msg}`)
            resolve(false)
          }
          reader.onerror = () => { resolve(false) }
          reader.readAsText(xhr.response)
        }
      }
      xhr.onerror = () => {
        clearPacking()
        alert('导出失败：网络错误或后端未启动')
        resolve(false)
      }
      xhr.ontimeout = () => {
        clearPacking()
        alert('导出失败：超时（超过 30 分钟）')
        resolve(false)
      }
      xhr.onabort = () => { clearPacking(); resolve(false) }
      xhr.send()
    })

    if (ok) toast('📤 导出成功，zip 已开始下载')
  } catch (e) {
    alert('导出失败：' + e.message)
  } finally {
    clearPacking()
    // 2.5s 后清理按钮状态
    setTimeout(() => {
      const next = { ...exportingId.value }
      delete next[lessonId]
      exportingId.value = next
      const nextP = { ...exportPercent.value }
      delete nextP[lessonId]
      exportPercent.value = nextP
      const nextPh = { ...exportPhase.value }
      delete nextPh[lessonId]
      exportPhase.value = nextPh
      const nextT = { ...exportText.value }
      delete nextT[lessonId]
      exportText.value = nextT
    }, 2500)
  }
}

function onImportClick() {
  importFileInput.value?.click()
}

async function onImportFile(e) {
  const file = e.target.files?.[0]
  if (!file) return
  importing.value = true
  importError.value = false
  importPercent.value = 0
  importPhase.value = 'upload'
  importText.value = '⬆️ 准备上传…'

  let processTimer = null
  const clearProcessTimer = () => {
    if (processTimer) { clearInterval(processTimer); processTimer = null }
  }
  const resetState = () => {
    clearProcessTimer()
    importing.value = false
    importPhase.value = ''
    importText.value = ''
    importPercent.value = 0
    importError.value = false
    e.target.value = ''  // 允许再次选同一个文件
  }

  try {
    await new Promise((resolve, reject) => {
      const fd = new FormData()
      fd.append('file', file)
      fd.append('pack_id', packId)

      const xhr = new XMLHttpRequest()
      xhr.open('POST', '/api/lessons/import', true)
      xhr.timeout = 30 * 60 * 1000  // 最长 30 分钟（大视频zip解压写入要时间）
      // responseType 设成 blob 以便读 500 等错误体
      xhr.responseType = 'blob'

      // 真实上传进度
      xhr.upload.onprogress = (ev) => {
        if (ev.lengthComputable) {
          // 上传占 0~70%，之后服务器处理占 70~100
          const uploadPct = ev.loaded / ev.total
          importPercent.value = Math.round(uploadPct * 70)
          const mb = (ev.loaded / 1024 / 1024).toFixed(1)
          const totalMb = (ev.total / 1024 / 1024).toFixed(1)
          importText.value = `⬆️ 上传中 ${mb} / ${totalMb} MB`
        } else {
          importText.value = '⬆️ 上传中…'
        }
      }
      xhr.upload.onload = () => {
        // 上传完成，进入服务器处理阶段（解压+写入DB+落盘视频字幕）
        importPhase.value = 'process'
        importPercent.value = 72
        importText.value = '📂 服务器处理中：解压文件…'
        let step = 0
        const stages = [
          '📂 服务器处理中：解压文件…',
          '📂 服务器处理中：写入数据库…',
          '📂 服务器处理中：拷贝字幕…',
          '📂 服务器处理中：写入视频…',
          '📂 服务器处理中：收尾中…',
        ]
        processTimer = setInterval(() => {
          step++
          const cur = importPercent.value
          if (cur < 98) {
            // 72% 起步，慢慢推到 98%（永远不会到100，等接口返回）
            const remain = 98 - cur
            const inc = remain > 5 ? 1 : 0.5
            importPercent.value = Math.min(98, cur + inc)
          }
          const stageIdx = Math.min(stages.length - 1, Math.floor(step / 4))
          importText.value = stages[stageIdx]
        }, 1500)
      }
      xhr.upload.onerror = () => {
        reject(new Error('上传失败：网络错误'))
      }

      xhr.onload = async () => {
        clearProcessTimer()
        const status = xhr.status
        const blob = xhr.response
        const text = await blob.text()
        let data = {}
        try { data = JSON.parse(text) } catch {}
        if (status >= 200 && status < 300) {
          importPercent.value = 100
          importPhase.value = 'done'
          importText.value = '✅ 导入成功'
          toast(data.message || '导入成功')
          resolve(data)
        } else {
          reject(new Error(data.message || data.detail || `HTTP ${status}`))
        }
      }
      xhr.onerror = () => {
        clearProcessTimer()
        reject(new Error('导入失败：网络错误或后端未启动'))
      }
      xhr.ontimeout = () => {
        clearProcessTimer()
        reject(new Error('导入失败：超时（超过 30 分钟）'))
      }
      xhr.onabort = () => {
        clearProcessTimer()
        reject(new Error('已取消'))
      }
      xhr.send(fd)
    })
    await loadLessons()
  } catch (err) {
    importError.value = true
    importText.value = '❌ ' + err.message
    toast('导入失败：' + err.message)
  } finally {
    if (importError.value) {
      // 出错：5 秒后重置，让用户看清错误
      setTimeout(resetState, 5000)
    } else {
      // 成功：2 秒后重置
      setTimeout(resetState, 2000)
    }
  }
}

// ============== 辅助 ==============
function stepText(step) {
  return ['⏳ 待处理', '🎵 抽音频', '🗣 ASR处理', '📝 格式化', '✅ 完成'][step] || '未知'
}
function stepClass(step) {
  if (step >= 4) return 'done'
  if (step >= 1) return 'running'
  return 'pending'
}
function learnText(l) {
  if (l.learned) return '✅ 已学'
  const pos = l.playback_position || 0
  const dur = l.duration_sec || 0
  if (pos > 1 && dur > 0) {
    const pct = Math.min(99, Math.round(pos / dur * 100))
    return `📖 ${pct}%`
  }
  return '⏳ 未学'
}
function learnClass(l) {
  if (l.learned) return 'learned'
  if ((l.playback_position || 0) > 1) return 'learning'
  return 'unlearned'
}
function fmtDate(ts) {
  if (!ts) return '—'
  const d = new Date(ts * 1000)
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0')
}

onMounted(async () => {
  await Promise.all([loadPack(), loadLessons()])
  // 若有正在处理的任务，每 3s 刷新一次列表，让用户看到 step 变化
  setInterval(() => {
    if (lessons.value.some(l => (l.upload_step || 0) > 0 && (l.upload_step || 0) < 4)) {
      loadLessons()
    }
  }, 3000)
})
</script>

<style scoped>
.pack-edit-page { display: flex; flex-direction: column; gap: 16px; }
.page-head {
  display: flex; align-items: center; gap: 12px; flex-wrap: wrap;
}
.page-head h2 { margin: 0; font-size: 22px; flex: 1; }
.pack-desc-row { display: flex; gap: 8px; }
.mute { color: var(--mute); font-size: 13px; }

.section-head {
  display: flex; align-items: center; gap: 12px; margin-bottom: 10px;
}
.section-head b { font-size: 15px; }

.upload-section, .lessons-section {
  padding: 16px; border: 1px solid var(--line); border-radius: 12px;
  background: var(--panel); backdrop-filter: blur(10px);
}
.hint {
  color: var(--mute); font-size: 12px; margin: 8px 0;
  line-height: 1.6;
}

.empty {
  padding: 30px; text-align: center; color: var(--mute);
  border: 1px dashed var(--line); border-radius: 10px;
}

.lessons-table {
  width: 100%; border-collapse: collapse; font-size: 13px;
}
.lessons-table th, .lessons-table td {
  padding: 10px 8px; text-align: left;
  border-bottom: 1px solid var(--line);
}
.lessons-table th {
  color: var(--mute); font-weight: 500; font-size: 12px;
  text-transform: uppercase; letter-spacing: 0.5px;
}
.lessons-table tr:hover td { background: #1a255044; }
.title-cell { max-width: 280px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.md5-cell { font-family: ui-monospace, Consolas, monospace; color: var(--mute); font-size: 12px; }
.date-cell { color: var(--mute); font-size: 12px; white-space: nowrap; }
.op-cell { white-space: nowrap; }
.op-cell button { padding: 4px 10px; font-size: 12px; margin-right: 4px; }
.danger-btn { color: var(--bad); }
.danger-btn:hover { border-color: var(--bad); background: #2a0f1f55; }
.warn-btn { color: var(--warn); }
.warn-btn:hover:not(:disabled) { border-color: var(--warn); background: #2a250f55; }
.warn-btn:disabled { opacity: .5; cursor: not-allowed; }

.section-actions { display: flex; gap: 8px; align-items: center; margin-left: auto; }
.import-btn { color: var(--good); border-color: #1a4a3a; }
.import-btn:hover:not(:disabled) { border-color: var(--good); background: #0e2a1f55; }
.import-btn:disabled { opacity: .5; cursor: not-allowed; }
.export-btn { color: var(--accent, #6cf); border-color: #1a3a5a; }
.export-btn:hover:not(:disabled) { border-color: var(--accent, #6cf); background: #0e2a4a55; }
.export-btn:disabled { opacity: .5; cursor: not-allowed; }

.step-chip {
  display: inline-block; padding: 3px 10px; border-radius: 999px;
  font-size: 12px; white-space: nowrap;
}
.step-chip.pending { background: #1a2550; color: var(--mute); }
.step-chip.running { background: #4a3a0e; color: var(--warn); }
.step-chip.done    { background: #0e3a1f; color: var(--good); }

.learn-chip {
  display: inline-block; padding: 3px 10px; border-radius: 999px;
  font-size: 12px; white-space: nowrap;
}
.learn-chip.learned    { background: #0e3a1f; color: var(--good); }
.learn-chip.learning  { background: #4a3a0e; color: var(--warn); }
.learn-chip.unlearned { background: #1a2550; color: var(--mute); }

:deep(.upload-box) { max-width: 100%; margin-top: 10px; }

/* ========= 导入/导出进度条样式 ========= */
.io-progress-box {
  margin-top: 10px;
  padding: 10px 14px;
  border: 1px solid var(--line);
  border-radius: 10px;
  background: #0e1530;
}
.io-progress-row { display: flex; align-items: center; gap: 6px; margin-bottom: 6px; }
.io-chip {
  display: inline-block;
  padding: 3px 12px;
  border-radius: 999px;
  font-size: 13px;
  background: #1a3a5a;
  color: #93c5fd;
  white-space: nowrap;
}
.io-chip.done { background: #0e3a1f; color: #86efac; }
.io-chip.error { background: #3a0e1a; color: #fca5a5; }

.io-progress, .mini-progress {
  width: 100%;
  height: 8px;
  background: #1a2550;
  border-radius: 999px;
  overflow: hidden;
}
.io-progress > span, .mini-progress > span {
  display: block;
  height: 100%;
  background: linear-gradient(90deg, #6cf, #4a7fcc);
  border-radius: 999px;
  transition: width .3s ease;
}

/* 每行操作区：按钮 + 导出进度条叠放 */
.op-cell .op-stack { display: flex; flex-direction: column; gap: 4px; }
.op-cell .op-btns { display: flex; flex-wrap: wrap; gap: 4px; align-items: center; }
.op-cell .op-btns button { padding: 4px 10px; font-size: 12px; }
.export-progress { display: flex; align-items: center; gap: 8px; }
.export-progress .mini-progress { height: 5px; flex: 1; }
.export-pct { font-size: 11px; color: var(--mute); min-width: 36px; text-align: right; }
</style>
